import { Component, ComponentChild, toChildArray } from 'preact';
import { Properties } from '../common';
import { EventListenerRemover, isClient, isFunction, isNotPresent, isServer, values } from '../utils';
import { CssModule, CssModuleApi, StyleContext, StyleRegistry, StyleSheet } from './types';

export interface StyleProviderState {
  registry: StyleRegistry;
}
export class StyleProvider extends Component<Properties, StyleProviderState> {
  private eventListenerRemovers: EventListenerRemover[] = [];
  private listeners: { [id: string]: (styleRegistry: StyleRegistry) => void } = {};
  private serverRegistry: StyleRegistry = {};

  constructor(props: Properties, context: StyleContext) {
    super(props, context);
    this.state = { registry: {} };
  }

  getChildContext(): StyleContext {
    const styleRegistry = isServer() ? this.serverRegistry : this.state.registry;
    return {
      styles: {
        registerModules: this.registerModules,
        addListener: (fn) => {
          const id = Date.now();
          this.listeners[id] = fn;
          fn(styleRegistry);

          return id;
        },
        removeListener: (id): void => {
          delete this.listeners[id];
        },
      },
    };
  }

  componentWillUnmount(): void {
    this.eventListenerRemovers
      .filter(isFunction)
      .forEach((eventListenerRemover: EventListenerRemover) => eventListenerRemover());
    this.eventListenerRemovers = [];

    this.listeners = {};
    this.setState(() => ({ registry: {} }));
    this.serverRegistry = undefined;
  }

  render({ children }: Properties): ComponentChild {
    const childArray = toChildArray(children);
    return (childArray && childArray[0]) || null;
  }

  private updateStyleSheet = (styleSheet: StyleSheet): void => {
    if (isServer()) {
      this.serverRegistry[styleSheet.id] = styleSheet;
      this.runListeners(this.serverRegistry);
    } else {
      this.setState((prevState) => {
        const newState = {
          ...prevState,
          registry: {
            ...prevState.registry,
            [styleSheet.id]: styleSheet,
          },
        };

        this.runListeners(newState.registry);

        return newState;
      });
    }
  };

  private registerModules = (modules: CssModule[]): void => {
    if (isServer()) {
      this.serverRegistry = modules
        .map((module: CssModule) => module._api)
        .filter((api: CssModuleApi) => isNotPresent(this.serverRegistry[api.content().id]))
        .reduce((newRegistry, api: CssModuleApi) => {
          const styleSheet: StyleSheet = api.content();
          newRegistry[styleSheet.id] = styleSheet;

          if (isFunction(api.addChangeListener) && isClient()) {
            this.eventListenerRemovers.push(api.addChangeListener(this.updateStyleSheet));
          }

          return newRegistry;
        }, this.serverRegistry);

      this.runListeners(this.serverRegistry);
    } else {
      this.setState((prevState) => {
        const registry = modules
          .map((module: CssModule) => module._api)
          .filter((api: CssModuleApi) => isNotPresent(prevState.registry[api.content().id]))
          .reduce((newRegistry, api: CssModuleApi) => {
            const styleSheet: StyleSheet = api.content();
            newRegistry[styleSheet.id] = styleSheet;

            if (isFunction(api.addChangeListener) && isClient()) {
              this.eventListenerRemovers.push(api.addChangeListener(this.updateStyleSheet));
            }

            return newRegistry;
          }, prevState.registry);

        this.runListeners(registry);

        return {
          ...prevState,
          registry,
        };
      });
    }
  };

  private runListeners(registry: StyleRegistry) {
    values(this.listeners).forEach((fn) => {
      fn(registry);
    });
  }
}
